<script lang="ts" setup>
import { useSwitchChain } from '@wagmi/vue';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import { useNuxtApp } from '#app';
import type { EvmChainId } from '~/service/EVM/config/types';
import { useUserStore } from '~/stores/user/useUserStore';
import { showCurrencyIcon } from '~/utils/helpers';

const userStore = useUserStore();

const { chainId } = storeToRefs(userStore);

const willBeUpdatedId = ref<Nullable<User['chainId']>>();

const { '$wagmi': { 'networksList': { setVisibleNetworkList } } } = useNuxtApp();

const { connector: { methods: { clearRpcErrors } } } = useProviderConfig()(['connector']);

const {
    chains,
    switchChain,
    isPending
} = useSwitchChain({
    mutation: {
        onSuccess: () => {
            willBeUpdatedId.value = null;
            setVisibleNetworkList(false);
        },
        onError: () => {
            willBeUpdatedId.value = null;
        }
    }
});

const onSwitchNetwork = (id: User['chainId']) => {
    clearRpcErrors();
    if (id !== chainId.value) {
        willBeUpdatedId.value = id;
        switchChain({ chainId: id });
    }
};

</script>

<template>
  <div v-bind="$attrs"
       class="chain-list"
  >
    <h5 class="chain-list__title compact-400-default">
      Select network
    </h5>
    <div v-for="({ id, nativeCurrency, name }) in chains"
         :key="id"
         class="display-flex align-items-center justify-between chain-list-row"
         @click="onSwitchNetwork(id as User['chainId'])"
    >
      <div class="display-flex align-items-center gap-12">
        <div class="chain-list-row__icon display-flex align-items-center justify-center">
          <IconMain :icon="showCurrencyIcon(nativeCurrency.symbol, id as EvmChainId)"
                    :size="20"
          />
        </div>

        <span class="text-500-default">{{ name }}</span>
      </div>
      <div v-if="!isPending && willBeUpdatedId !== id"
           class="display-flex align-items-center justify-center"
           :class="[
             {'chain-list-row__check': chainId === id},
             {'chain-list-row__outline': chainId !== id},
           ]"
      >
        <IconMain v-if="chainId === id"
                  icon="check"
                  :size="24"
        />
      </div>
      <div v-else-if="isPending && willBeUpdatedId === id">
        <LoaderDots />
      </div>
    </div>
  </div>
</template>

<style lang="scss">

%spacing-y {
  padding-top: $spacing-600;
  padding-bottom: $spacing-600;

  @include tablet-768 {
    padding-top: 0;
    padding-bottom: 0;
  }
}

%spacing-x {
  padding-right: $spacing-600;
  padding-left: $spacing-600;

  @include tablet-768 {
    padding-right: $spacing-400;
    padding-left: $spacing-400;
  }
}

.chain-list {
  width: 348px;
  border-radius: var(--background-500-radius, 4px);
  background: var(--layer-floor-2);

  @extend %spacing-y;

  @include tablet-768 {
    width: 100%;
    background: var(--layer-floor-1);
  }

  &__title {
    @extend %spacing-x;

    margin-bottom: $spacing-200;
    color: #B2B2B5;
  }

  &-row {
    display: flex;
    align-items: center;
    align-self: stretch;
    padding-top: 10px;
    padding-bottom: 10px;
    transition: background-color 0.3s ease;
    cursor: pointer;

    @include tablet-768 {
      padding-left: 48px;
    }

    @extend %spacing-x;

    &--disabled {
      pointer-events: none;
    }

    &:hover {
      background-color: var(--bg-active-elements);
    }

    &__check {
      width: 28px;
      min-width: 28px;
      height: 28px;
      min-height: 28px;
      border-radius: 50%;
      background-color: var(--bg-brand-aviation-primary);
    }

    &__outline {
      width: 28px;
      min-width: 28px;
      height: 28px;
      min-height: 28px;
      border: solid 1px var(--border-primary);
      border-radius: 50%
    }

    &__icon {
      width: 36px;
      height: 36px;
      border-radius: var(--control-400-radius, 2px);
      background-color: var(--bg-tertary);

      @include tablet-768 {
        width: 28px;
        height: 28px;

        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
  }
}
</style>
